import React, { useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { ErrorMessage, useField } from 'formik';
import { useColorScheme } from 'nativewind';
import { cln } from '../../utils/classnames';
import Icon from '../Icon';
import { SvgIcon } from '../../assets';

interface Props {
  title: string;
  name: string;
  required?: boolean;
  placeholder?: string;
  trailingIcon?: SvgIcon;
  helperText?: string;
}

const FormInput: React.FC<Props> = ({
  title,
  name,
  required = false,
  placeholder,
  trailingIcon,
  helperText,
}) => {
  const { colorScheme } = useColorScheme();
  const [field, meta, helpers] = useField(name);

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const isDarkMode = colorScheme === 'dark';
  const needsDarkBackground = isDarkMode === isFocused;

  return (
    <View className="mb-11 relative">
      <View className="flex-row">
        <Text className="text-input text-neutral-950 dark:text-neutral-50 font-[sans-600]">
          {title}
        </Text>
        {required && <Text className="text-input text-danger-500 font-[sans-400]"> *</Text>}
      </View>

      <View className="relative">
        <TextInput
          value={field.value ? field.value : ''}
          className={cln(
            isFocused
              ? 'text-neutral-50 bg-neutral-950 dark:text-neutral-950 dark:bg-neutral-50'
              : 'text-neutral-950 bg-neutral-50 dark:text-neutral-50 dark:bg-neutral-950',
            !!meta.touched && !!meta.error
              ? 'border-danger-500'
              : 'border-neutral-900 dark:border-neutral-200',
            'h-12 border-[1px] flex-1 p-3  outline-none',
          )}
          style={{ outline: 'none' } as object}
          keyboardType="ascii-capable"
          placeholderTextColor={needsDarkBackground ? '#101010' : '#888888'}
          onChangeText={helpers.setValue}
          onBlur={() => {
            setIsFocused(false);
            field.onBlur(name);
          }}
          focusable={false}
          onFocus={() => {
            setIsFocused(true);
          }}
          submitBehavior="blurAndSubmit"
          onSubmitEditing={() => setIsFocused(false)}
          placeholder={placeholder}
          tabIndex={0}
        />
        {trailingIcon && (
          <Icon
            classNames="absolute right-3 top-[9px]"
            icon={trailingIcon}
            mobileSize={{ width: 24, height: 24 }}
            webSize={{ width: 24, height: 24 }}
          />
        )}
      </View>
      <ErrorMessage name={name}>
        {(message) => (
          <Text className="text-notificationMessage text-danger-500 font-[sans-600] absolute top-[68px]">
            {message}
          </Text>
        )}
      </ErrorMessage>
      {helperText ? (
        <Text className="text-notificationMessage text-neutral-950 dark:text-neutral-50 font-[sans-600] mt-[6px]">
          {helperText}
        </Text>
      ) : (
        <></>
      )}
    </View>
  );
};

export default FormInput;
